/**
 * THE HIVEMIND 🧠 - Benchmark Data Panel
 * 
 * Displays industry benchmarks that power the Council's debates.
 * When gods cite numbers, this is where they come from.
 */

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORY_ICONS = {
  cac: '💰',
  budget: '📊',
  email: '📧',
  social: '📱',
  reddit: '🔴',
  conversion: '🎯',
  paid_ads: '💳',
  seo: '🔍',
  content_roi: '📈',
  growth: '🚀',
  failure: '⚠️',
  viral: '🔥',
  financial: '💵'
};

const HivemindPanel = ({ isOpen, onClose }) => {
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareValue, setCompareValue] = useState('');
  const [compareResult, setCompareResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/war-council/hivemind/categories`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (err) {
      console.error('Failed to fetch Hivemind categories:', err);
    }
  };

  const fetchCategoryData = async (category) => {
    setIsLoading(true);
    setSelectedCategory(category);
    try {
      const response = await fetch(`${API_URL}/api/war-council/hivemind/data/${category}`);
      const data = await response.json();
      if (data.success) {
        setCategoryData(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch category data:', err);
    }
    setIsLoading(false);
  };

  const compareToHivemind = async () => {
    if (!compareValue || !selectedCategory) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/war-council/hivemind/compare?category=${selectedCategory}&metric=average&your_value=${compareValue}`
      );
      const data = await response.json();
      if (data.success) {
        setCompareResult(data);
      }
    } catch (err) {
      console.error('Compare failed:', err);
    }
  };

  const renderValue = (value, key) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
      if ('min' in value && 'max' in value) {
        return `${value.min} - ${value.max}`;
      }
      return JSON.stringify(value);
    }
    // Add % for rate/pct values
    if (key.includes('rate') || key.includes('pct') || key.includes('percentage')) {
      return `${value}%`;
    }
    return value.toString();
  };

  const renderDataSection = (data, title = '') => {
    if (!data || typeof data !== 'object') return null;
    
    return (
      <div className="space-y-2">
        {title && <h4 className="text-sm font-semibold text-purple-400 mb-2">{title}</h4>}
        <div className="grid gap-2">
          {Object.entries(data).map(([key, value]) => {
            if (key === 'source') {
              return (
                <div key={key} className="text-xs text-gray-500 italic mt-2">
                  Source: {value}
                </div>
              );
            }
            
            if (typeof value === 'object' && !('min' in value)) {
              return (
                <div key={key} className="mt-3">
                  {renderDataSection(value, key.replace(/_/g, ' ').toUpperCase())}
                </div>
              );
            }
            
            return (
              <div key={key} className="flex justify-between items-center bg-gray-800/50 px-3 py-2 rounded">
                <span className="text-gray-400 text-sm">
                  {key.replace(/_/g, ' ')}
                </span>
                <span className="text-white font-medium">
                  {renderValue(value, key)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-purple-500/30 rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-purple-900/50 to-indigo-900/50">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <span className="text-2xl">🧠</span> The Hivemind
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Industry benchmarks that power the Council's debates
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Category Sidebar */}
          <div className="w-64 border-r border-gray-700 overflow-y-auto bg-gray-800/30">
            <div className="p-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Data Categories
              </h3>
              <div className="space-y-1">
                {Object.entries(categories).map(([key, info]) => (
                  <button
                    key={key}
                    onClick={() => fetchCategoryData(key)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all ${
                      selectedCategory === key
                        ? 'bg-purple-500/20 border border-purple-500/50 text-purple-400'
                        : 'hover:bg-gray-700/50 text-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{CATEGORY_ICONS[key] || '📊'}</span>
                      <span className="text-sm font-medium">{info.name}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 ml-6">
                      {info.gods_who_use?.map(g => g.charAt(0).toUpperCase() + g.slice(1)).join(', ')}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Data Panel */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full" />
              </div>
            ) : !selectedCategory ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="text-6xl mb-4">🧠</div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Select a Category
                </h3>
                <p className="text-gray-400 max-w-md">
                  The Hivemind contains industry benchmarks from HubSpot, Unbounce, 
                  WordStream, Sprout Social, and more. When the gods cite numbers, 
                  this is where they come from.
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">
                    {CATEGORY_ICONS[selectedCategory]} {categories[selectedCategory]?.name}
                  </h3>
                </div>
                
                <p className="text-gray-400 text-sm mb-4">
                  {categories[selectedCategory]?.description}
                </p>

                {/* Compare Mode Toggle */}
                <div className="mb-4 p-3 bg-gray-800/50 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={compareMode}
                      onChange={(e) => setCompareMode(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-300">Compare your metrics</span>
                  </label>
                  
                  {compareMode && (
                    <div className="mt-3 flex gap-2">
                      <input
                        type="number"
                        value={compareValue}
                        onChange={(e) => setCompareValue(e.target.value)}
                        placeholder="Enter your value"
                        className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                      />
                      <button
                        onClick={compareToHivemind}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded text-sm"
                      >
                        Compare
                      </button>
                    </div>
                  )}
                  
                  {compareResult && (
                    <div className={`mt-3 p-3 rounded ${
                      compareResult.status === 'above_benchmark' 
                        ? 'bg-green-500/20 border border-green-500/30' 
                        : compareResult.status === 'below_benchmark'
                        ? 'bg-red-500/20 border border-red-500/30'
                        : 'bg-blue-500/20 border border-blue-500/30'
                    }`}>
                      <div className="text-sm font-medium text-white">
                        Your value: {compareResult.your_value} vs Benchmark: {compareResult.benchmark_value}
                      </div>
                      <div className={`text-sm mt-1 ${
                        compareResult.status === 'above_benchmark' 
                          ? 'text-green-400' 
                          : compareResult.status === 'below_benchmark'
                          ? 'text-red-400'
                          : 'text-blue-400'
                      }`}>
                        {compareResult.difference_pct > 0 ? '+' : ''}{compareResult.difference_pct}% | {compareResult.interpretation}
                      </div>
                    </div>
                  )}
                </div>

                {/* Data Display */}
                {categoryData && renderDataSection(categoryData)}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-700 bg-gray-800/30 text-center">
          <p className="text-xs text-gray-500">
            Data compiled from HubSpot, Unbounce, WordStream, Sprout Social, SaaS Capital, and industry reports | Last updated: January 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default HivemindPanel;
